import FileSaver from 'file-saver';
import { useMutation, useQuery } from 'react-query';

import {
  acceptAgreement,
  downloadContractUnderReview,
  generateContract,
  getFeesAndCharges,
  getNafathStatus,
  getSignatories,
  getSignatoriesInfo,
  getTermsAndConditions,
  resetAttempts,
  sendInvitation,
  sendInvitationKsa,
  setSignatory,
  uploadSignedContract,
} from '~/api/contract';
import { NafathStep } from '~/pages/ksa/contract/components/NafathIntegration/NafathModal';
import { ApiError } from '~/types/ApiError';
import { NafathUpload, NafathApiErrorDetails, NafathStatus } from '~/types/Contract';
import { ImageResponse } from '~/types/files';
import { isAgent } from '~/utils/getDecodedJwt';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useGenerateContract = () => {
  return useMutation(generateContract);
};

export const useUploadSignedContract = () => {
  return useMutation<NafathUpload, ApiError<NafathApiErrorDetails>, ImageResponse[]>(
    uploadSignedContract,
  );
};


export const useCheckNafathStatus = (
  onSuccess: (data: NafathStatus) => void,
  currentStep?: NafathStep,
  isEnabled?: any
) => {
  return useQuery('nafathStatus', getNafathStatus, {
    refetchInterval: currentStep === 'NAFATH_NUMBER' ? 3000 : false,
    onSuccess: (data) => onSuccess(data),
  });
};

export const useSendInvitation = () => {
  return useMutation(sendInvitation);
};

export const useSendInvitationKsa = () => {
  return useMutation(sendInvitationKsa);
};

export const useSetSignatory = () => {
  return useMutation(setSignatory);
};

/**
 * Calls POST /stages/ksa/contract/accept-agreement.
 * Should be invoked when the merchant clicks Submit on the Agreement page
 * (after checking T&C and Fees & Charges checkboxes).
 */
export const useAcceptAgreement = () => {
  return useMutation<NafathUpload, ApiError<NafathApiErrorDetails>, undefined>(acceptAgreement);
};

export const useGetSignatoriesInfo = (isEnabled?: boolean) => {
  return useQuery('signatoriesInfo', getSignatoriesInfo, {
    enabled: isAgent() && isEnabled,
  });
};

export const useResetAttempts = () => {
  return useMutation(resetAttempts);
};

export const useGetSignatories = () => {
  return useQuery('signatories', getSignatories, {
    select: (data) => data.signatories,
    enabled: !isAgent(),
  });
};

export const useDownloadContractUnderReview = () => {
  const mutation = useMutation(downloadContractUnderReview);

  const download = async () => {
    const { blob, name } = await mutation.mutateAsync();
    FileSaver.saveAs(blob, name);
  };

  return { ...mutation, download };
};

/**
 * Fetches Terms & Conditions content from the API.
 * The query key includes the current language so the cache is per-language.
 * When the user switches language, a fresh API call is made automatically.
 * TODO: Update the API URL in api/contract.ts when the real endpoint is available.
 */
export const useGetTermsAndConditions = ({ enabled = true } = {}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  return useQuery(
    ['ksaTermsAndConditions', lang],
    () => getTermsAndConditions(lang),
    {
      enabled,           // only fetches when enabled=true (i.e. not readOnly)
      staleTime: Infinity, // cache per language per session
      retry: 1,
    }
  );
};

/**
 * Fetches Fees & Charges content from the API.
 * The query key includes the current language so the cache is per-language.
 * When the user switches language, a fresh API call is made automatically.
 * TODO: Update the API URL in api/contract.ts when the real endpoint is available.
 */
export const useGetFeesAndCharges = (enabled = true) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  return useQuery(
    ['ksaFeesAndCharges', lang],
    () => getFeesAndCharges(lang),
    {
      enabled,
      staleTime: Infinity, // cache per language per session
      retry: 1,
    }
  );
};
